import { cn } from '@/lib/utils';

// ── Card marks, drawn not typed ───────────────────────────────────────────────
// These were the characters ☸ (wheel of dharma), ☀ (sun) and 〰 (wavy dash).
// They looked right on Windows/Chrome and WRONG on an iPhone: iOS system fonts
// carry no glyph for any of them, so Safari fell through to Apple Color Emoji
// and painted a purple squircle emblem, a cartoon sun and an emoji squiggle.
// A variation selector (U+FE0E) does not help — nothing in the text font chain
// has the glyph to select. Drawing them as SVG removes the font from the problem
// entirely, so the card is byte-identical on every device.

/** National emblem, stood in for by the dharma wheel it contains. */
function EmblemMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6L5.6 18.4" />
      <circle cx="12" cy="12" r="2.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** UIDAI mark — the sun beside the red brush stroke. */
function AadhaarMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 34 20" className={className} fill="none" aria-hidden="true">
      <circle cx="8" cy="9" r="3.6" fill="#F59E0B" />
      <g stroke="#F59E0B" strokeWidth="1.4" strokeLinecap="round">
        <path d="M8 1.4v1.6M8 15v1.6M1.4 9H3M13 9h1.6M3.4 4.4l1.1 1.1M11.5 12.5l1.1 1.1M12.6 4.4l-1.1 1.1M4.5 12.5l-1.1 1.1" />
      </g>
      <path
        d="M17 11.5c1.6-3.4 3.2 3.4 4.8 0s3.2 3.4 4.8 0 3.2 3.4 4.8 0"
        stroke="#B91C1C"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Mock UIDAI Aadhaar card — the Aadhaar counterpart of PAN's IdCardMock.
// Deliberately styled like a real Aadhaar card (white base, tricolour band,
// national emblem, Aadhaar logo, red tagline) so it reads differently from the
// blue PAN card. Uses the same dummy person photo as PAN (/pan-card-photo.png).
//
// Sizing:
//   • default  — phone-preview size (builder screens).
//   • compact  — shrinks the number so it fits the narrower OCR scan frame.
//   • large    — the real applicant screen (and the admin detail view).
//
// Why `large` alone is FLUID (clamp + vw) while the others are fixed px:
// `large` is the only variant whose width really tracks the viewport, so it is
// the only one that has to survive every phone. It previously used fixed
// 72px photo / 76px QR / 11px text, which is fine at 390px and overflows at 320px
// — a 320px screen leaves the middle column about 42px, and "Name: Arun Kumar"
// needs ~95px, so it was clipped by the card's rounded edge. The clamps shrink
// the photo, QR, gaps and text together as the screen narrows, and cap at the
// old fixed values so a wide phone and the desktop detail view look unchanged.
// The default/compact variants live inside the builder's FIXED-width phone frame,
// where vw tracks the browser window rather than the frame — so vw would be
// actively wrong there, and they stay in px.
const FLUID = {
  gap: 'gap-[clamp(8px,3vw,16px)]',
  photo: 'w-[clamp(56px,17vw,72px)]',
  qr: 'h-[clamp(58px,18vw,76px)] w-[clamp(58px,18vw,76px)]',
  label: 'text-[clamp(9px,2.7vw,11px)]',
  number: 'text-[clamp(15px,5vw,20px)] tracking-[clamp(1px,0.5vw,2px)]',
} as const;

export function AadhaarCardMock({
  compact = false,
  large = false,
}: {
  compact?: boolean;
  large?: boolean;
}) {
  return (
    <div
      className={cn(
        'relative flex w-full flex-col overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-sm',
        large ? 'p-4' : 'p-[10px]',
      )}
    >
      {/* Header: emblem · tricolour band · Aadhaar logo */}
      <div className={cn('flex items-center gap-2', large ? 'mb-3' : 'mb-2')}>
        {/* National emblem (stylised) */}
        <div className="flex shrink-0 flex-col items-center leading-none text-[#374151]">
          <EmblemMark className={large ? 'h-[18px] w-[18px]' : 'h-[14px] w-[14px]'} />
          <span className={cn('font-semibold', large ? 'text-[6px]' : 'text-[5px]')}>
            सत्यमेव जयते
          </span>
        </div>
        {/* Tricolour brush band */}
        <div className="flex flex-1 flex-col gap-[3px]">
          <div className={cn('w-full rounded-full bg-[#FF9933]', large ? 'h-[7px]' : 'h-[6px]')} />
          <div
            className={cn(
              'text-center font-bold leading-none text-[#111827]',
              large ? 'text-[10px]' : 'text-[8px]',
            )}
          >
            भारत सरकार · GOVERNMENT OF INDIA
          </div>
          <div className={cn('w-full rounded-full bg-[#138808]', large ? 'h-[7px]' : 'h-[6px]')} />
        </div>
        {/* Aadhaar logo: yellow sun + red brush stroke */}
        <div className="flex shrink-0 flex-col items-center leading-none">
          <AadhaarMark className={large ? 'h-[16px] w-[27px]' : 'h-[13px] w-[22px]'} />
          <span className={cn('font-bold text-[#B91C1C]', large ? 'text-[8px]' : 'text-[7px]')}>
            आधार
          </span>
        </div>
      </div>

      {/* `min-w-0` releases the flex min-content floor: the rows below are
          `whitespace-nowrap`, and without it this row refuses to shrink under its
          content width, so on a phone the middle column pushed the QR — and the
          number — straight out through the card's clipped right edge. */}
      <div
        className={cn('relative z-10 flex min-w-0 flex-1 items-start', large ? FLUID.gap : 'gap-3')}
      >
        <div
          className={cn(
            'h-full shrink-0 overflow-hidden rounded-md border border-[#D1D5DB] bg-[#94A3B8]',
            large ? FLUID.photo : 'w-[54px]',
          )}
        >
          <img src="/pan-card-photo.png" alt="" className="h-full w-full object-cover" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          {[
            ['Name:', 'Arun Kumar'],
            ['DOB:', '15/08/1995'],
            ['Gender:', 'Male'],
          ].map(([label, val]) => (
            <div
              key={label}
              className={cn(
                'flex flex-row items-center gap-1 whitespace-nowrap',
                large ? 'mb-1.5' : 'mb-1',
              )}
            >
              <span
                className={cn(
                  'font-medium leading-none text-[#4B5563]',
                  large ? FLUID.label : 'text-[9px]',
                )}
              >
                {label}
              </span>
              <span
                className={cn(
                  'font-bold leading-none text-[#111827]',
                  large ? FLUID.label : 'text-[9px]',
                )}
              >
                {val}
              </span>
            </div>
          ))}
        </div>
        <div
          className={cn(
            'shrink-0 rounded-md border border-[#E5E7EB] bg-white',
            large ? `${FLUID.qr} p-1.5` : 'h-[60px] w-[60px] p-1',
          )}
        >
          <img src="/pan-card-qr.png" alt="" className="h-full w-full object-cover" />
        </div>
      </div>

      {/* The number spans the whole card, centred — which is where a real Aadhaar
          card carries it, and it is also the only place it CAN'T overflow. It used
          to sit inside the details column beside the QR, where 14 nowrap characters
          at 20px needed ~180px in a column that gets ~110px on a 390px phone; the
          card clipped the tail of it ("…1234" ran off the edge). */}
      <div
        className={cn(
          'relative z-10 text-center font-extrabold leading-none text-[#111827]',
          large
            ? `mt-3 ${FLUID.number}`
            : compact
              ? 'mt-2 text-[12px] tracking-tight'
              : 'mt-2 text-[14px] tracking-[1px]',
        )}
      >
        XXXX XXXX 1234
      </div>

      {/* Red baseline + tagline like the real card */}
      <div
        className={cn(
          'relative z-10 border-t-2 border-[#B91C1C] text-center font-semibold tracking-wide text-[#B91C1C]',
          large ? 'mt-3 pt-1.5 text-[11px]' : 'mt-2 pt-1 text-[9px]',
        )}
      >
        मेरा आधार, मेरी पहचान
      </div>
    </div>
  );
}
